import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, Card, Column, Divider, IconButton, Row, Typography, useOverlayContext } from 'seagull';
import { ArrowRight, Kebab } from 'seagull/icons';
import { getAgreementPdfUrl } from '~/business-logic/relationshipLogic';
import RecipientDetails from '~/components/RecipientDetails/RecipientDetails';
import { useIoContext, useStore } from '~/contexts';
import { relationshipModel } from '~/models';
import { fromDateStampObject } from '~/models/time';
import { selectors } from '~/store/state-selectors';
import { formatLongMonthUSDate } from '~/utils';
import { getLimitedTimeDiscountOfferCalloutText } from '../../WelcomeStep/welcomeStepUtils';
import { GetCoverPageTID } from './CoverPage';
import styles from './CoverPageMobile.module.scss';
import { InviteMemberActionsMenu } from './InviteMemberBottomSheetFlow';
export const CoverPageMobile = (param)=>{
    let { testId, relationship, onGetStarted, isPreview } = param;
    var _relationship_signatories;
    const { CoverPageTestIds } = useMemo(()=>GetCoverPageTID(testId), [
        testId
    ]);
    const vendorName = relationship.vendor.name;
    const clientName = relationship.customer.name;
    const vendorLogoUrl = relationshipModel.vendorLogoUrl({
        vendor: relationship.vendor
    });
    const { showBottomSheet } = useOverlayContext();
    const { openNewTab } = useIoContext();
    const pdfHost = useStore(selectors.pdfHost);
    const expirationDate = relationship.expirationDate ? fromDateStampObject(relationship.expirationDate) : null;
    const discountCalloutText = getLimitedTimeDiscountOfferCalloutText(relationship);
    const hasExpirationInfo = expirationDate || discountCalloutText;
    const onDownloadPdf = ()=>{
        openNewTab(getAgreementPdfUrl(relationshipModel.id(relationship), pdfHost));
    };
    const openActionsMenu = ()=>{
        showBottomSheet({
            content: /*#__PURE__*/ _jsx(InviteMemberActionsMenu, {
                onDownloadPdf: onDownloadPdf
            }),
            variant: 'branded'
        });
    };
    return /*#__PURE__*/ _jsx(Card, {
        padding: "m",
        className: styles.card,
        testId: CoverPageTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Column, {
            className: styles.container,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    className: styles.header,
                    justifyContent: "space-between",
                    alignItems: "start",
                    children: [
                        vendorLogoUrl ? /*#__PURE__*/ _jsx("img", {
                            "data-testid": CoverPageTestIds.VendorLogo(),
                            src: vendorLogoUrl,
                            alt: vendorName,
                            className: styles.logo
                        }) : /*#__PURE__*/ _jsx(Typography, {
                            variant: "h5",
                            weight: "bold",
                            children: vendorName
                        }),
                        !isPreview && /*#__PURE__*/ _jsx(IconButton, {
                            skin: "primaryTransparent",
                            size: "smallMedium",
                            className: styles.menuIconButton,
                            onClick: openActionsMenu,
                            children: /*#__PURE__*/ _jsx(Kebab, {})
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    className: styles.content,
                    children: [
                        /*#__PURE__*/ _jsxs(Column, {
                            gap: 4,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: CoverPageTestIds.ProposalTitle(),
                                    variant: "h2",
                                    weight: "bold",
                                    fontFamily: "hero",
                                    children: "Proposal for review"
                                }),
                                /*#__PURE__*/ _jsxs(Typography, {
                                    testId: CoverPageTestIds.ProposalPreparedFor(),
                                    variant: "h5",
                                    weight: "normal",
                                    fontFamily: "hero",
                                    children: [
                                        "Prepared for ",
                                        clientName
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Typography, {
                                    testId: CoverPageTestIds.PropsalDescription(),
                                    children: [
                                        vendorName,
                                        " has sent you this proposal and is requesting that you sign and accept it."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Divider, {
                            margin: 16
                        }),
                        /*#__PURE__*/ _jsxs(Column, {
                            gap: 8,
                            className: styles.recipientsSection,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: CoverPageTestIds.RecipientsTitle(),
                                    variant: "paragraph2",
                                    skin: "secondary",
                                    children: "Recipients"
                                }),
                                /*#__PURE__*/ _jsx(RecipientDetails, {
                                    className: styles.recipient,
                                    firstName: relationship.buyer.firstName,
                                    lastName: relationship.buyer.lastName,
                                    badge: "required",
                                    requiredBadgeSkin: "primary",
                                    requiredBadgeClassName: styles.requiredBadge
                                }),
                                (_relationship_signatories = relationship.signatories) === null || _relationship_signatories === void 0 ? void 0 : _relationship_signatories.map((signatory)=>/*#__PURE__*/ _jsx(RecipientDetails, {
                                        testId: CoverPageTestIds.Recipient(signatory.id),
                                        className: styles.recipient,
                                        firstName: signatory.firstName,
                                        lastName: signatory.lastName,
                                        badge: relationshipModel.hasSignatorySigned(relationship, signatory.id) ? 'signed' : 'required',
                                        requiredBadgeSkin: "primary",
                                        requiredBadgeClassName: styles.requiredBadge
                                    }, signatory.id)),
                                relationship.reviewers.map((reviewer)=>/*#__PURE__*/ _jsx(RecipientDetails, {
                                        testId: CoverPageTestIds.Reviewer(reviewer.id),
                                        className: styles.recipient,
                                        firstName: reviewer.firstName,
                                        lastName: reviewer.lastName
                                    }, reviewer.id))
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    className: styles.footer,
                    gap: 16,
                    children: [
                        hasExpirationInfo && /*#__PURE__*/ _jsxs(Typography, {
                            variant: "paragraph2",
                            testId: CoverPageTestIds.ProposalExpirationDate(),
                            children: [
                                expirationDate && "This proposal is valid until ".concat(formatLongMonthUSDate(expirationDate), ". "),
                                discountCalloutText
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            testId: CoverPageTestIds.GetStarted(),
                            suffixIcon: /*#__PURE__*/ _jsx(ArrowRight, {
                                size: 16
                            }),
                            onClick: onGetStarted,
                            className: styles.button,
                            children: "Get started"
                        })
                    ]
                })
            ]
        })
    });
};
